/**
 * Port of Angular 17.3's `DomSanitizer.sanitize(SecurityContext.HTML, value)` (`@angular/core` html_sanitizer.ts,
 * MIT), which @kolkov/angular-editor runs over its HTML on every change before the value reaches the form model.
 *
 * It is a port of the algorithm and not of DOMPurify's behaviour because the saved string has to match Angular's
 * byte for byte: same element / attribute allow-lists (no `style`, `id`, `contenteditable`, ...), attribute order as
 * parsed, and Angular's entity encoding (`&#160;` for a non-breaking space, `&#10;` for a line feed, `&#34;` for a
 * double quote) that a browser's `innerHTML` serialisation does not produce.
 */
const tagSet = (tags: string): Record<string, true> => Object.fromEntries(tags.split(',').map((tag) => [tag, true]));
const merge = (...sets: Record<string, true>[]): Record<string, true> => Object.assign({}, ...sets);

const VOID_ELEMENTS = tagSet('area,br,col,hr,img,wbr');
const OPTIONAL_END_TAG_BLOCK_ELEMENTS = tagSet('colgroup,dd,dt,li,p,tbody,td,tfoot,th,thead,tr');
const OPTIONAL_END_TAG_INLINE_ELEMENTS = tagSet('rp,rt');
const OPTIONAL_END_TAG_ELEMENTS = merge(OPTIONAL_END_TAG_INLINE_ELEMENTS, OPTIONAL_END_TAG_BLOCK_ELEMENTS);
const BLOCK_ELEMENTS = merge(
  OPTIONAL_END_TAG_BLOCK_ELEMENTS,
  tagSet(
    'address,article,aside,blockquote,caption,center,del,details,dialog,dir,div,dl,figure,figcaption,footer,h1,h2,h3,h4,h5,h6,header,hgroup,hr,ins,main,map,menu,nav,ol,pre,section,summary,table,ul',
  ),
);
const INLINE_ELEMENTS = merge(
  OPTIONAL_END_TAG_INLINE_ELEMENTS,
  tagSet(
    'a,abbr,acronym,audio,b,bdi,bdo,big,br,cite,code,del,dfn,em,font,i,img,ins,kbd,label,map,mark,picture,q,ruby,rp,rt,s,samp,small,source,span,strike,strong,sub,sup,time,track,tt,u,var,video',
  ),
);
const VALID_ELEMENTS = merge(VOID_ELEMENTS, BLOCK_ELEMENTS, INLINE_ELEMENTS, OPTIONAL_END_TAG_ELEMENTS);

const URI_ATTRS = tagSet('background,cite,href,itemtype,longdesc,poster,src,xlink:href');
const HTML_ATTRS = tagSet(
  'abbr,accesskey,align,alt,autoplay,axis,bgcolor,border,cellpadding,cellspacing,class,clear,color,cols,colspan,' +
    'compact,controls,coords,datetime,default,dir,download,face,headers,height,hidden,hreflang,hspace,' +
    'ismap,itemscope,itemprop,kind,label,lang,language,loop,media,muted,nohref,nowrap,open,preload,rel,rev,role,rows,rowspan,rules,' +
    'scope,scrolling,shape,size,sizes,span,srclang,srcset,start,summary,tabindex,target,title,translate,type,usemap,' +
    'valign,value,vspace,width',
);
const ARIA_ATTRS = tagSet(
  'aria-activedescendant,aria-atomic,aria-autocomplete,aria-busy,aria-checked,aria-colcount,aria-colindex,' +
    'aria-colspan,aria-controls,aria-current,aria-describedby,aria-details,aria-disabled,aria-dropeffect,' +
    'aria-errormessage,aria-expanded,aria-flowto,aria-grabbed,aria-haspopup,aria-hidden,aria-invalid,' +
    'aria-keyshortcuts,aria-label,aria-labelledby,aria-level,aria-live,aria-modal,aria-multiline,' +
    'aria-multiselectable,aria-orientation,aria-owns,aria-placeholder,aria-posinset,aria-pressed,aria-readonly,' +
    'aria-relevant,aria-required,aria-roledescription,aria-rowcount,aria-rowindex,aria-rowspan,aria-selected,' +
    'aria-setsize,aria-sort,aria-valuemax,aria-valuemin,aria-valuenow,aria-valuetext',
);
const VALID_ATTRS = merge(URI_ATTRS, HTML_ATTRS, ARIA_ATTRS);
const SKIP_TRAVERSING_CONTENT_IF_INVALID_ELEMENTS = tagSet('script,style,template');

const SAFE_URL_PATTERN = /^(?!javascript:)(?:[a-z0-9+.-]+:|[^&:/?#]*(?:[/?#]|$))/i;
const sanitizeUrl = (url: string): string => (SAFE_URL_PATTERN.test(String(url)) ? String(url) : `unsafe:${url}`);

const SURROGATE_PAIR_REGEXP = /[\uD800-\uDBFF][\uDC00-\uDFFF]/g;
const NON_ALPHANUMERIC_REGEXP = /([^#-~ |!])/g;

/** Angular's `encodeEntities`. */
export function encodeEntities(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(SURROGATE_PAIR_REGEXP, (match) => {
      const hi = match.charCodeAt(0);
      const low = match.charCodeAt(1);
      return `&#${(hi - 0xd800) * 0x400 + (low - 0xdc00) + 0x10000};`;
    })
    .replace(NON_ALPHANUMERIC_REGEXP, (match) => `&#${match.charCodeAt(0)};`)
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Angular's DOMParser-based inert body: `<body><remove></remove>` + html, the marker child removed again. */
function inertBody(html: string): HTMLElement {
  const body = new DOMParser().parseFromString(`<body><remove></remove>${html}`, 'text/html').body;
  body.firstChild?.remove();
  return body;
}

function serialise(root: Node): string {
  const buf: string[] = [];
  let current: Node | null = root.firstChild;
  let traverseContent = true;
  const parents: Node[] = [];
  while (current) {
    if (current.nodeType === Node.ELEMENT_NODE) {
      const element = current as Element;
      const tagName = element.nodeName.toLowerCase();
      if (Object.hasOwn(VALID_ELEMENTS, tagName)) {
        buf.push('<', tagName);
        for (const attr of Array.from(element.attributes)) {
          const lower = attr.name.toLowerCase();
          if (!Object.hasOwn(VALID_ATTRS, lower)) continue;
          const value = Object.hasOwn(URI_ATTRS, lower) ? sanitizeUrl(attr.value) : attr.value;
          buf.push(' ', attr.name, '="', encodeEntities(value), '"');
        }
        buf.push('>');
        traverseContent = true;
      } else {
        traverseContent = !Object.hasOwn(SKIP_TRAVERSING_CONTENT_IF_INVALID_ELEMENTS, tagName);
      }
    } else if (current.nodeType === Node.TEXT_NODE) {
      buf.push(encodeEntities(current.nodeValue ?? ''));
    }
    if (traverseContent && current.firstChild) {
      parents.push(current);
      current = current.firstChild;
      continue;
    }
    while (current) {
      if (current.nodeType === Node.ELEMENT_NODE) {
        const tagName = current.nodeName.toLowerCase();
        if (Object.hasOwn(VALID_ELEMENTS, tagName) && !Object.hasOwn(VOID_ELEMENTS, tagName)) {
          buf.push('</', tagName, '>');
        }
      }
      const next: Node | null = current.nextSibling;
      if (next) {
        current = next;
        break;
      }
      current = parents.pop() ?? null;
    }
  }
  return buf.join('');
}

/** `DomSanitizer.sanitize(SecurityContext.HTML, html)`; null in, null out. */
export function angularSanitizeHtml(value: string | null | undefined): string | null {
  if (value == null) return null;
  let unsafeHtml = String(value);
  let body = inertBody(unsafeHtml);
  // mXSS protection: parse repeatedly until the markup is stable.
  let attempts = 5;
  let parsedHtml = unsafeHtml;
  do {
    if (attempts === 0) throw new Error('Failed to sanitize html because the input is unstable');
    attempts -= 1;
    unsafeHtml = parsedHtml;
    parsedHtml = body.innerHTML;
    body = inertBody(unsafeHtml);
  } while (unsafeHtml !== parsedHtml);
  return serialise(body);
}
